import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TestemunhoService } from './testemunho.service';
import { NotificationService } from '../../core/notification.service';

@Component({
  selector: 'app-testemunho-manager',
  standalone: false,
  template: `
    <div class="mt-8 bg-white border border-border rounded shadow p-4">
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-lg font-semibold">Testemunhos</h3>
        <span class="text-xs text-muted">{{ items.length }} registro(s)</span>
      </div>
      <div *ngIf="items.length === 0" class="text-sm text-muted mb-4">Nenhum testemunho cadastrado.</div>
      <div class="space-y-3 mb-4" *ngIf="items.length > 0">
        <div *ngFor="let t of items" class="border border-border rounded p-3 relative group">
          <div class="flex justify-between items-start gap-3">
            <div class="flex-1">
              <div class="font-medium text-sm">{{ t.nome }}</div>
              <div class="text-xs text-muted">{{ t.createdAt | date:'dd/MM/yyyy HH:mm' }}</div>
            </div>
            <div class="flex gap-2">
              <button (click)="edit(t)" class="text-xs border border-border px-2 py-1 rounded hover:bg-surface" title="Editar">✏️</button>
              <button (click)="remove(t)" class="text-xs bg-danger text-white px-2 py-1 rounded hover:bg-danger/80" title="Remover">🗑️</button>
            </div>
          </div>
          <p class="mt-2 text-sm whitespace-pre-line">{{ t.descricao }}</p>
        </div>
      </div>
      <form [formGroup]="form" (ngSubmit)="save()" class="space-y-3 bg-surface/50 p-3 rounded">
        <h4 class="text-sm font-medium mb-2">{{ editingItem ? 'Editar Testemunho' : 'Novo Testemunho' }}</h4>
        <div>
          <label class="block text-xs font-medium">Nome *</label>
          <input formControlName="nome" class="w-full border border-border rounded px-2 py-1 text-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium">Depoimento *</label>
          <textarea formControlName="descricao" rows="3" class="w-full border border-border rounded px-2 py-1 text-sm"></textarea>
          <div class="text-right text-[10px] text-muted">{{ form.controls['descricao'].value?.length || 0 }}/2000</div>
        </div>
        <div class="flex justify-end gap-2">
          <button *ngIf="editingItem" type="button" (click)="cancelEdit()" class="border border-border px-3 py-1 rounded text-sm">Cancelar</button>
          <button type="submit" [disabled]="form.invalid || saving" class="bg-primary text-white px-3 py-1 rounded text-sm">{{ editingItem ? 'Atualizar' : 'Adicionar' }}</button>
        </div>
      </form>
    </div>
  `
})
export class TestemunhoManagerComponent implements OnInit {
  @Input() fornecedorId!: string;
  items: any[] = [];
  form!: FormGroup;
  saving = false;
  loading = false;
  editingItem: any = null;

  constructor(private fb: FormBuilder,
              private svc: TestemunhoService,
              private notification: NotificationService) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      nome: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(200)]],
      descricao: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(2000)]]
    });
    if (this.fornecedorId) this.load();
  }

  load() {
    if (!this.fornecedorId) return;
    this.loading = true;
    this.svc.listByFornecedor(this.fornecedorId, 1, 50).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.items = res?.data || [];
      },
      error: (err: any) => {
        this.loading = false;
        console.error('[TestemunhoManager] falha ao listar', err);
        this.notification.showError('Erro ao listar testemunhos');
      }
    });
  }

  save() {
    if (this.form.invalid || !this.fornecedorId) return;
    this.saving = true;
    const payload = { ...this.form.value, fornecedorId: this.fornecedorId };
    
    if (this.editingItem) {
      // Update existing
      this.svc.adminUpdate(this.editingItem.id, payload).subscribe({
        next: (updated: any) => {
          this.saving = false;
          const idx = this.items.findIndex(i => i.id === this.editingItem.id);
          if (idx >= 0) this.items[idx] = updated;
          this.form.reset();
          this.editingItem = null;
          this.notification.showSuccess('Testemunho atualizado');
        },
        error: (err: any) => {
          this.saving = false;
          console.error('[TestemunhoManager] erro ao atualizar', err);
          this.handleFormErrors(err);
          this.notification.showError('Erro ao atualizar testemunho');
        }
      });
    } else {
      // Create new
      this.svc.create(payload).subscribe({
        next: (t: any) => {
          this.saving = false;
          this.items = [t, ...this.items];
          this.form.reset();
          this.notification.showSuccess('Testemunho adicionado');
        },
        error: (err: any) => {
          this.saving = false;
          console.error('[TestemunhoManager] erro ao criar', err);
          this.handleFormErrors(err);
          this.notification.showError('Erro ao adicionar testemunho');
        }
      });
    }
  }

  edit(item: any) {
    this.editingItem = item;
    this.form.patchValue({ nome: item.nome, descricao: item.descricao });
  }

  cancelEdit() {
    this.editingItem = null;
    this.form.reset();
  }

  private handleFormErrors(err: any) {
    if (err && err.validation && err.validation.fieldErrors) {
      const fields = err.validation.fieldErrors;
      Object.keys(fields).forEach(k => {
        const c = this.form.get(k.toLowerCase()) || this.form.get(k);
        if (c) c.setErrors({ server: fields[k].join(' ') });
      });
    }
  }

  remove(t: any) {
    if (!confirm('Remover este testemunho?')) return;
    this.svc.adminDelete(t.id).subscribe({
      next: () => {
        this.items = this.items.filter(i => i.id !== t.id);
        this.notification.showSuccess('Removido');
      },
      error: (err: any) => {
        console.error('[TestemunhoManager] erro ao remover', err);
        this.notification.showError('Erro ao remover testemunho');
      }
    });
  }
}
